import React, { useCallback, useEffect, useState, useRef } from "react";
import NextWeekIcon from "@mui/icons-material/NextWeek";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import toast from "react-hot-toast";
import { MAIN_URL } from "../../../Configurations/Urls";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import Layout4 from "../../DataLayouts/Layout4";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  fetchEmployeRecord,
} from "../../../Apis/Employee-api";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import AlarmAddIcon from "@mui/icons-material/AlarmAdd";
import LogoutIcon from "@mui/icons-material/Logout";
import { operatorRequiresValue } from "../../../Components/filters/operators";
import { fetchOrganizationDepartment } from "../../../Apis/Department-api";
import { fetchIncrementTypes } from "../../../Apis/Salary";
import {
  applyClientToolbarFilters,
  asOptionList,
  mergeToolbarFilters,
  ToolbarFilterSelect,
  useToolbarFilterChange,
} from "../../../Components/Table/toolbarFilters";

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrev: false,
};

const DEFAULT_URL_PARAMS = {
  page: "1",
  pageSize: "10",
  sortBy: "functional_role_name",
  sortOrder: "desc",
};

const RECORD_TOOLBAR_FILTERS = [
  { urlKey: "department_id", field: "organization_department_id" },
  {
    urlKey: "increment_type_id",
    field: "organization_employee_increment_type_id",
  },
];

// Helper functions
const buildFiltersFromParams = (searchParams) => {
  const filters = [];
  const processedFields = new Set();

  searchParams.forEach((value, key) => {
    if (
      key.endsWith("_operator") ||
      key.endsWith("_logic") ||
      !key.startsWith("filter_")
    )
      return;

    const field = key.replace("filter_", "");
    if (processedFields.has(field)) return;
    processedFields.add(field);

    const operator = searchParams.get(`filter_${field}_operator`) || "contains";
    const requiresValue = operatorRequiresValue(operator);

    if (!requiresValue || value) {
      let filterValue = requiresValue ? value : null;

      if (requiresValue && filterValue) {
        if (["equals", "not_equals"].includes(operator)) {
          filterValue = filterValue
            .split(",")
            .map((v) => v.trim().toLowerCase());
        } else if (["between", "not_between"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => v.trim());
        }
      }

      filters.push({ field, operator, value: filterValue });
    }
  });

  const search = searchParams.get("search");
  if (search) {
    filters.push({
      field: "employee_name",
      operator: "contains",
      value: search,
    });
  }

  return filters;
};

const DEFAULT_COLUMNS = [
  {
    field: "employee_name",
    label: "Employee Name",
    visible: true,
    width: 200,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "employee_code",
    label: "Employee Code",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "designation",
    label: "Designation",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "department",
    label: "Department",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "increment_type",
    label: "Increment Type",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
];

function EmployeeRecordList() {
  const [exit, setexit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);
  const [tableConfig, setTableConfig] = useState(null);
  const [configColumns, setConfigColumns] = useState(DEFAULT_COLUMNS);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [incrementTypes, setIncrementTypes] = useState([]);
  const { userData } = useAuthStore();

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);
  const org = userData?.organization;
  const handleToolbarFilterChange = useToolbarFilterChange(setSearchParams);

  // Load table configuration from general-datagrids API
  useEffect(() => {
    const loadTableConfiguration = async () => {
      if (!org?.organization_id) {
        setLoadingConfig(false);
        return;
      }

      try {
        const configRes = await fetch(
          `${MAIN_URL}/api/general-datagrids`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          }
        );

        if (configRes.ok) {
          const configResponse = await configRes.json();
          const datagrids = configResponse.datagrids || [];
          const orgKey = `employee_employment_records_grid_${org.organization_id}`;
          const savedConfig = datagrids.find(
            (dg) => dg.datagrid_key === orgKey
          );

          if (savedConfig) {
            const serverCfg = savedConfig.datagrid_default_configuration;
            setTableConfig(serverCfg);

            if (
              serverCfg?.columns &&
              Array.isArray(serverCfg.columns) &&
              serverCfg.columns?.length > 0
            ) {
              setConfigColumns(serverCfg.columns);
            } else {
              setConfigColumns(DEFAULT_COLUMNS);
            }
          } else {
            setConfigColumns(DEFAULT_COLUMNS);
          }
        }
      } catch (error) {
        console.error("Error loading table configuration:", error);
        setConfigColumns(DEFAULT_COLUMNS);
      } finally {
        setLoadingConfig(false);
      }
    };

    loadTableConfiguration();
  }, [org?.organization_id]);

  // Initialize URL parameters
  useEffect(() => {
    if (hasInitializedURL.current) return;

    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;

    Object.entries(DEFAULT_URL_PARAMS).forEach(([key, defaultValue]) => {
      if (!params.has(key)) {
        params.set(key, defaultValue);
        needsUpdate = true;
      }
    });

    if (needsUpdate) {
      setSearchParams(params, { replace: true });
    }

    hasInitializedURL.current = true;
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!org?.organization_id) return;
    let cancelled = false;
    const loadOptions = async () => {
      try {
        const [deptRes, incrementRes] = await Promise.allSettled([
          fetchOrganizationDepartment(org.organization_id),
          fetchIncrementTypes(org.organization_id),
        ]);
        if (cancelled) return;
        if (deptRes.status === "fulfilled") {
          setDepartments(asOptionList(deptRes.value, ["Departments", "data"]));
        }
        if (incrementRes.status === "fulfilled") {
          setIncrementTypes(
            asOptionList(incrementRes.value, ["increments", "data"])
          );
        }
      } catch (error) {
        console.error("Failed to load employment record toolbar filters", error);
      }
    };
    loadOptions();
    return () => {
      cancelled = true;
    };
  }, [org?.organization_id]);

  // Load employee records data
  useEffect(() => {
    if (!org?.organization_id || !hasInitializedURL.current) return;

    fetchControllerRef.current?.abort();
    fetchControllerRef.current = new AbortController();

    setLoading(true);

    const fetchData = async () => {
      try {
        const filters = mergeToolbarFilters(
          buildFiltersFromParams(searchParams),
          searchParams,
          RECORD_TOOLBAR_FILTERS
        );
        const requestBody = {
          filters,
          page: Number.parseInt(searchParams.get("page"), 10) || 1,
          pageSize: Number.parseInt(searchParams.get("pageSize"), 10) || 10,
          sort: {
            field: searchParams.get("sortBy") || "functional_role_name",
            direction: searchParams.get("sortOrder") || "desc",
          },
        };

        // Try new POST endpoint first, fallback to old GET endpoint if needed
        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org.organization_id}/employee-records-filtered`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
            signal: fetchControllerRef.current.signal,
          }
        );

        let data;
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            data = result.data;
            
            if (result.pagination) {
              const pageSize =
                result.pagination.pageSize || result.pagination.limit || 10;
              setPaginationData({
                page:
                  result.pagination.page ||
                  Number.parseInt(searchParams.get("page"), 10) ||
                  1,
                pageSize,
                total: result.pagination.total || 0,
                totalPages: result.pagination.totalPages || 1,
                hasNext: result.pagination.hasNext || false,
                hasPrev: result.pagination.hasPrev || false,
              });
            }
          } else {
            data = [];
            setPaginationData(DEFAULT_PAGINATION);
          }
        } else if (response.status === 404) {
          // Fallback to old GET endpoint if POST endpoint doesn't exist yet
          const oldData = await fetchEmployeRecord(org.organization_id);
          data = applyClientToolbarFilters(
            oldData?.records?.data || oldData?.records || [],
            searchParams,
            RECORD_TOOLBAR_FILTERS
          );
          setPaginationData(DEFAULT_PAGINATION);
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Transform data - preserve existing transformation logic
        const b = data.map((item) => {
          return {
            ...item,
            id: item.employee_employment_record_id,
          };
        });

        setexit(b);
      } catch (error) {
        if (error.name === "AbortError") {
          return; // Request was cancelled, ignore
        }
        console.error("Error fetching employee records:", error);
        toast.error("Failed to load employee records");
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      fetchControllerRef.current?.abort();
    };
  }, [org?.organization_id, searchParams]);

  const handleDelete = useCallback(
    async (id) => {
      try {
        const org_id = org.organization_id;
        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org_id}/employee-records/${id}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (response.ok) {
          const result = await response.json();
          toast.success(result.message || "Employee record deleted successfully");
          // Refresh data by updating search params
          const params = new URLSearchParams(searchParams);
          setSearchParams(params);
        } else {
          const errorData = await response.json();
          if (response.status === 401) {
            toast.error("Session Expired!");
            window.location.href = "/login";
          } else {
            const errorMessage =
              errorData.message ||
              errorData.errors?.[Object.keys(errorData.errors || {})[0]]?.[0] ||
              "Failed to delete Employee Record";
            toast.error(errorMessage);
          }
        }
      } catch (error) {
        console.error("Delete failed:", error);
        toast.error("Something went wrong. Please try again later.");
      }
    },
    [org?.organization_id, searchParams, setSearchParams]
  );

  const handleEdit = useCallback(
    (item) => {
      navigate(
        `/organization/employee/records/edit/${item.id || item.employee_employment_record_id}`
      );
    },
    [navigate]
  );

  const handleShow = useCallback(
    (item) => {
      navigate(
        `/organization/employee/records/view/${item.id || item.employee_employment_record_id}`
      );
    },
    [navigate]
  );

  return (
    <>
      <Layout4
        loading={loading}
        heading={"Employment Records"}
        btnName={"Add Records"}
        add_action={"EMPLOYEE_RECORD_ADD"}
        delete_action={"EMPLOYEE_RECORD_DELETE"}
        Data={exit}
        tableHeaders={[
          { name: "Code", value_key: "employee_code", width: "50px" },
          { name: "Name", value_key: "name", width: "150px" },

          {
            name: "NoticePeriod Starts",
            value_key: "notice_period_start",
            width: "170px",
          },

          {
            name: "NoticePeriod Ends",
            value_key: "notice_period_end",
            width: "170px",
          },
          {
            name: "Resignation Date",
            value_key: "resignation_date",
            width: "150px",
          },
          {
            name: "Relieving Date",
            value_key: "relieving_date",
            width: "150px",
          },
          {
            name: "Interview",
            value_key: "exit_interview_done",
            width: "50px",
          },
        ]}
        Icons={[
          <AutorenewIcon sx={{ fontSize: 60, color: "grey.500", mb: 2 }} />,
          <NextWeekIcon color="primary" />,
          <AlarmAddIcon sx={{ color: "text.secondary" }} />,
          <LogoutIcon sx={{ color: "text.secondary" }} />,
        ]}
        messages={[
          "Employee exit details",
          "Employee exit details",
          "Add Employee Exit Details",
          "Employee Exit Deatils",
        ]}
        Route={"/organization/employee/records"}
        setData={setexit}
        DeleteFunc={handleDelete}
      />

      {!loadingConfig && (
        <TableDataGeneric
          tableName="employee_employment_records"
          primaryKey="employee_employment_record_id"
          heading="Employee Record"
          data={exit}
          sortname="change_reason"
          showActions={true}
          Route="/organization/employee/records"
          DeleteFunc={handleDelete}
          edit_delete_action={["EMPLOYEE_RECORD_DELETE", "EMPLOYEE_RECORD_EDIT"]}
          EditFunc={handleEdit}
          handleShow={handleShow}
          token={localStorage.getItem("token")}
          configss={configColumns}
          paginationData={paginationData}
          filterModule="employee_employment_records"
          toolbarExtras={
            <>
              <ToolbarFilterSelect
                label="Department"
                urlKey="department_id"
                allLabel="All departments"
                options={departments}
                valueKey="organization_department_id"
                labelKey="department_name"
                searchParams={searchParams}
                onChange={handleToolbarFilterChange}
              />
              <ToolbarFilterSelect
                label="Increments"
                urlKey="increment_type_id"
                allLabel="All increments"
                options={incrementTypes}
                valueKey="organization_employee_increment_type_id"
                labelKey="employee_increment_type_name"
                searchParams={searchParams}
                onChange={handleToolbarFilterChange}
              />
            </>
          }
          {...(tableConfig && { config: tableConfig })}
        />
      )}
    </>
  );
}

export default EmployeeRecordList;